import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Grid,
  IconButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  CreditCard as CreditCardIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useSettings } from '../contexts/SettingsContext';
import { driversAPI, ridesAPI, carTypesAPI, paymentAPI } from '../services/apiService';
import AddressAutocomplete from './AddressAutocomplete';
import AccountAutocomplete from './AccountAutocomplete';
import PaymentForm from './PaymentForm';
import { calculateRoute } from '../services/routeCalculator';
import { useGoogleMaps } from './GoogleMapsProvider';
import soundService from '../services/soundService';
import { safeInvoke } from '../utils/signalRHelper';
import { isAirport, getAirportRules, formatPhoneNumber, PRICING_DEFAULTS } from '../config/appConstants';


const NewCallWizard = ({ onComplete, onCancel }) => {
  const { user, signalRConnection, connectSignalR } = useAuth();
  const { showAlert, showToast } = useAlert();
  const { isLoaded: googleMapsLoaded } = useGoogleMaps();
  const [drivers, setDrivers] = useState([]);
  const { settings, addOns } = useSettings();
  const [carTypes, setCarTypes] = useState([]);

  const [formData, setFormData] = useState({
    // Ride object fields
    customerName: '',
    customerPhoneNumber: '',
    cost: 0,
    driversCompensation: 0,
    notes: '',
    paymentType: settings.defaultPaymentMethod || 'cash',
    passengers: 1,
    carType: '',
    selectedAddOnIds: [],
    assignedToId: null,
    assignedToName: '',
    flightNumber: '',

    // Route object fields
    pickup: '',
    dropOff: '',
    additionalStops: [],
    roundTrip: false,
    estimatedDuration: 0, // in minutes

    // Payment token (Square tokenization)
    paymentTokenId: null
  });

  const [errors, setErrors] = useState({});
  const phoneInputRef = useRef(null);
  const stopRefs = useRef([]);
  const squarePaymentRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [routeInfo, setRouteInfo] = useState({ distance: '', duration: '' });
  const [pricingDetails, setPricingDetails] = useState(null);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  // Track if user has manually edited the price (to avoid overwriting with recalculation)
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);
  // Track current rush hour surcharge applied to handle add/subtract on schedule change
  const [currentRushHourSurcharge, setCurrentRushHourSurcharge] = useState(0);
  // Track if addresses were selected from autocomplete (not just typed)
  const [addressesValidated, setAddressesValidated] = useState({ pickup: false, dropOff: false });
  // Track which stop indices have been validated (selected from autocomplete)
  const [stopsValidated, setStopsValidated] = useState([]);
  // Track if CC accordion is expanded (user is entering card info)
  const [ccAccordionExpanded, setCcAccordionExpanded] = useState(false);
  // Linked customer account (for autofill + discount)
  const [linkedAccount, setLinkedAccount] = useState(null);
  // When true, the account discount is intentionally waived for this ride
  const [skipAccountDiscount, setSkipAccountDiscount] = useState(false);
  // True when the payment token was auto-set from the account's saved card (so we clear it on unlink)
  const [cardFromAccount, setCardFromAccount] = useState(false);
  // Whether to save the manually-entered card to the linked account on submission
  const [saveCardToAccount, setSaveCardToAccount] = useState(false);
  const scheduleRef = useRef(null);

  // Fetch all drivers and car types on component mount
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const data = await driversAPI.getAll();
        setDrivers(data || []);
      } catch (error) {
        console.error('Error fetching drivers:', error);
      }
    };
    const fetchCarTypes = async () => {
      try {
        const data = await carTypesAPI.getAll();
        const sorted = (data || []).sort((a, b) => a.sortOrder - b.sortOrder);
        setCarTypes(sorted);
        if (sorted.length > 0) {
          setFormData(prev => ({ ...prev, carType: prev.carType || sorted[0].id }));
        }
      } catch (error) {
        console.error('Error fetching car types:', error);
      }
    };
    fetchDrivers();
    fetchCarTypes();
  }, []);

  // Phone number formatting - use shared utility
  // (formatPhoneNumber imported from appConstants)

  // Extract digits only from formatted phone number
  const extractPhoneDigits = (value) => {
    return value.replace(/\D/g, '');
  };

  // Check if an address is an airport (uses configurable rules from settings)
  const airportRules = getAirportRules(settings);
  const checkIsAirport = (address) => isAirport(address, airportRules);

  // Handle backspace on phone number formatting characters
  const handlePhoneBackspace = (e, fieldName) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const cursorPos = input.selectionStart;
      const value = input.value;
      const charBefore = value[cursorPos - 1];

      // If cursor is right after a formatting character, remove the digit before it
      if (cursorPos > 0 && (charBefore === '(' || charBefore === ')' || charBefore === ' ' || charBefore === '-')) {
        e.preventDefault();

        let removeCount;
        if (charBefore === ' ') {
          // Space after ): remove space, ), and digit (3 chars total if available)
          removeCount = Math.min(cursorPos, 3);
        } else if (charBefore === '(') {
          // Opening paren: remove ( and digit before it (or just ( if at start)
          removeCount = cursorPos >= 2 ? 2 : 1;
        } else {
          // ) or -: remove formatting char and digit before it
          removeCount = cursorPos >= 2 ? 2 : 1;
        }

        const newValue = value.slice(0, cursorPos - removeCount) + value.slice(cursorPos);
        handleInputChange(fieldName, newValue);
      }
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // For phone number, extract digits then format
    if (field === 'customerPhoneNumber') {
      // Get current cursor position before formatting
      const input = phoneInputRef.current;
      const cursorPos = input?.selectionStart || 0;
      const oldValue = formData.customerPhoneNumber || '';

      // Extract digits from both old and new values
      const oldDigits = extractPhoneDigits(oldValue);
      const newDigits = extractPhoneDigits(value);

      // Format the new value
      processedValue = formatPhoneNumber(newDigits);

      // Calculate new cursor position after formatting
      // If digits were removed (backspace), keep cursor at same position
      if (newDigits.length < oldDigits.length) {
        // User deleted a digit - maintain cursor position
        setTimeout(() => {
          if (input) {
            // Find the position in the new formatted string
            let newPos = cursorPos;
            // If cursor was after a formatting char that got removed, adjust
            if (processedValue[cursorPos - 1] === ')' ||
              processedValue[cursorPos - 1] === ' ' ||
              processedValue[cursorPos - 1] === '-') {
              newPos = cursorPos - 1;
            }
            input.setSelectionRange(newPos, newPos);
          }
        }, 0);
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // If user is typing in address fields, mark as not validated
    // (they need to select from autocomplete to validate)
    if (field === 'dropOff' || field === 'pickup') {
      setAddressesValidated(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  // Calculate route when pickup, dropoff, stops, or roundTrip changes
  const calculateTripRoute = async () => {
    // Only calculate if both addresses were selected from autocomplete
    if (!googleMapsLoaded || !formData.pickup || !formData.dropOff) {
      setRouteInfo({ distance: '', duration: '' });
      setFormData(prev => ({ ...prev, estimatedDuration: 0, cost: 0, driversCompensation: 0 }));
      setPricingDetails(null);
      return;
    }

    // Only calculate if both addresses are validated (selected from autocomplete)
    if (!addressesValidated.pickup || !addressesValidated.dropOff) {
      return;
    }

    setIsCalculatingRoute(true);
    setIsCalculatingPrice(true);
    try {
      // Only include stops that have been validated (selected from autocomplete)
      const validatedStops = formData.additionalStops.filter((s, index) =>
        s && s.trim() !== '' && stopsValidated.includes(index)
      );

      // Calculate route for distance/duration display
      const result = await calculateRoute(
        formData.pickup,
        formData.dropOff,
        validatedStops,
        formData.roundTrip
      );

      if (result.error) {
        setRouteInfo({ distance: '', duration: '' });
      } else {
        setRouteInfo({
          distance: result.distanceText,
          duration: result.durationText
        });

        setFormData(prev => ({
          ...prev,
          estimatedDuration: result.duration
        }));
      }

      // Call pricing API for accurate pricing (forceRecalc=true since route changed)
      await calculatePricing(validatedStops, true);
      // Reset manual edit flag since route fundamentally changed
      setPriceManuallyEdited(false);
    } catch (error) {
      // Silently handle errors
      setRouteInfo({ distance: '', duration: '' });
    } finally {
      setIsCalculatingRoute(false);
    }
  };

  // Calculate pricing using the server API
  const calculatePricing = async (validatedStops = [], forceRecalc = false) => {
    try {
      // Build scheduled time for rush hour calculation
      let scheduledDateTime = null;
      if (scheduledDate && scheduledTime) {
        scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }

      const priceRequest = {
        pickup: formData.pickup,
        dropOff: formData.dropOff,
        stops: validatedStops,
        carType: formData.carType || 1,
        isRoundTrip: formData.roundTrip,
        scheduledTime: scheduledDateTime
      };

      const response = await ridesAPI.calculatePrice(priceRequest);

      if (response.success) {
        const newRushHourSurcharge = response.rushHourSurcharge || 0;

        // If price was manually edited and this isn't a forced recalc (route change),
        // only adjust the rush hour difference
        if (priceManuallyEdited && !forceRecalc) {
          const rushHourDiff = newRushHourSurcharge - currentRushHourSurcharge;
          if (rushHourDiff !== 0) {
            setFormData(prev => ({
              ...prev,
              cost: Math.round(prev.cost + rushHourDiff),
              driversCompensation: Math.round(prev.driversCompensation + (rushHourDiff * (PRICING_DEFAULTS.rushHourDriverCompRatio)))
            }));
            setCurrentRushHourSurcharge(newRushHourSurcharge);
            console.log(`Rush hour adjustment: ${rushHourDiff > 0 ? '+' : ''}$${rushHourDiff}`);
          }
          setPricingDetails(prev => ({ ...prev, rushHourSurcharge: newRushHourSurcharge }));
        } else {
          // Full price update (initial calculation or route changed)
          setPricingDetails(response);
          setCurrentRushHourSurcharge(newRushHourSurcharge);
          // Add selected add-on fees
          const addOnsTotal = (formData.selectedAddOnIds || []).reduce(
            (sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0
          );
          const rawCost = Math.round(response.totalPrice) + addOnsTotal;
          // Apply account discount (if linked, not waived, and price not manually edited)
          let finalCost = rawCost;
          if (!skipAccountDiscount && linkedAccount && linkedAccount.discountType && linkedAccount.discountValue > 0) {
            if (linkedAccount.discountType === 'percent') {
              finalCost = Math.max(0, Math.round(rawCost * (1 - Math.min(linkedAccount.discountValue, 100) / 100)));
            } else {
              finalCost = Math.max(0, rawCost - linkedAccount.discountValue);
            }
          }
          setFormData(prev => ({
            ...prev,
            // Add-on fees go entirely to the driver, so add them to both cost and driver compensation
            cost: finalCost,
            driversCompensation: Math.round(response.driversCompensation) + addOnsTotal
          }));
        }
      } else {
        console.error('Pricing calculation failed:', response.error);
        // Only reset to 0 if this is initial calculation, not rush hour adjustment
        if (!priceManuallyEdited) {
          setPricingDetails(null);
          setFormData(prev => ({
            ...prev,
            cost: 0,
            driversCompensation: 0
          }));
        }
      }
    } catch (error) {
      console.error('Error calculating price:', error);
      if (!priceManuallyEdited) {
        setPricingDetails(null);
        setFormData(prev => ({
          ...prev,
          cost: 0,
          driversCompensation: 0
        }));
      }
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  // Trigger route calculation when route-related fields change (full recalculation)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.pickup && formData.dropOff && addressesValidated.pickup && addressesValidated.dropOff) {
        calculateTripRoute();
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timer);
  }, [formData.pickup, formData.dropOff, formData.additionalStops, formData.roundTrip, formData.carType, formData.selectedAddOnIds, googleMapsLoaded, addressesValidated, stopsValidated]);

  // Separate effect for schedule changes - only adjust rush hour surcharge, don't recalculate entire price
  useEffect(() => {
    // Only run if we have a valid route and pricing already calculated
    if (!formData.pickup || !formData.dropOff || !addressesValidated.pickup || !addressesValidated.dropOff) {
      return;
    }
    if (formData.cost === 0) {
      return; // No initial price calculated yet
    }

    const timer = setTimeout(async () => {
      setIsCalculatingPrice(true);
      const validatedStops = formData.additionalStops.filter((s, index) =>
        s && s.trim() !== '' && stopsValidated.includes(index)
      );
      // forceRecalc=false so it only adjusts rush hour if price was manually edited
      await calculatePricing(validatedStops, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [scheduledTime]);

  // Handle address selection from autocomplete
  const handleAddressSelect = (field, address, placeDetails) => {
    setFormData(prev => ({
      ...prev,
      [field]: address
    }));

    // Mark this address as validated (selected from autocomplete)
    setAddressesValidated(prev => ({
      ...prev,
      [field]: true
    }));

    // Clear error when address is selected
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle stop address selection
  const handleStopSelect = (index, address, placeDetails) => {
    const newStops = [...formData.additionalStops];
    newStops[index] = address;
    setFormData(prev => ({
      ...prev,
      additionalStops: newStops
    }));

    // Mark this stop as validated
    setStopsValidated(prev => {
      if (!prev.includes(index)) {
        return [...prev, index];
      }
      return prev;
    });
  };

  const addStop = () => {
    // Maximum 10 stops allowed (stop1 through stop10 in the database)
    if (formData.additionalStops.length >= 10) {
      showAlert('Maximum Stops', 'Maximum 10 stops allowed', [{ text: 'OK' }], 'warning');
      return;
    }
    const newStops = [...formData.additionalStops, ''];
    setFormData(prev => ({
      ...prev,
      additionalStops: newStops
    }));

    // Focus on the newly added stop after render
    setTimeout(() => {
      const lastIndex = newStops.length - 1;
      if (stopRefs.current[lastIndex]) {
        stopRefs.current[lastIndex].focus();
      }
    }, 100);
  };

  const removeStop = (index) => {
    const newStops = formData.additionalStops.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      additionalStops: newStops
    }));

    // Adjust stopsValidated: remove this index and decrement indices above it
    setStopsValidated(prev =>
      prev
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i)
    );
  };

  const updateStop = (index, value) => {
    const newStops = [...formData.additionalStops];
    newStops[index] = value;
    setFormData(prev => ({
      ...prev,
      additionalStops: newStops
    }));

    // When manually typed, invalidate this stop
    setStopsValidated(prev => prev.filter(i => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerPhoneNumber.trim()) {
      newErrors.customerPhoneNumber = 'Phone number is required';
    } else {
      // Validate phone format
      const phoneDigits = formData.customerPhoneNumber.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        newErrors.customerPhoneNumber = 'Phone number must be 10 digits';
      }
    }

    if (!formData.pickup.trim()) {
      newErrors.pickup = 'Pickup location is required';
    }

    if (!formData.dropOff.trim()) {
      newErrors.dropOff = 'Drop-off location is required';
    }

    if (!formData.cost || formData.cost <= 0) {
      newErrors.cost = 'Price must be greater than $0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildNewRideObject = () => {
    const now = new Date();
    const currentTime = now.toISOString();
    let scheduledFor = currentTime;
    if (scheduledDate && scheduledTime) {
      const localScheduledDate = new Date(`${scheduledDate}T${scheduledTime}`);
      scheduledFor = localScheduledDate.toISOString();
    }

    // Convert estimatedDuration (minutes) to TimeOnly format (HH:mm:ss)
    const durationMinutes = formData.estimatedDuration || 0;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = Math.floor(durationMinutes % 60);
    const estimatedDurationTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

    // Determine payment type and paid status
    const hasPaymentToken = !!formData.paymentTokenId;
    // Use selected payment type, or dispatcherCC if token exists, or default to cash
    const paymentType = hasPaymentToken ? 'dispatcherCC' : (formData.paymentType || 'cash');
    // Paid = true for Zelle (already paid externally) or if CC token exists (payment secured)
    const isPaid = paymentType === 'zelle' || hasPaymentToken;

    // Build a Ride object that matches the C# Ride class structure
    const rideObject = {
      rideId: 0,
      routeId: 0,
      customerName: formData.customerName,
      customerPhoneNumber: formData.customerPhoneNumber.replace(/\D/g, ''),
      callTime: currentTime,
      scheduledFor: scheduledFor,
      pickupTime: null,
      dropOffTime: null,
      cost: parseInt(formData.cost) || 0,
      driversCompensation: parseInt(formData.driversCompensation) || 0,
      paidTime: null,
      assignedToId: formData.assignedToId || null,
      reassignedToId: null,
      notes: formData.notes || "",
      dispatchedById: user?.userId || null,
      paymentType: paymentType,
      paymentTokenId: formData.paymentTokenId || null,
      paid: isPaid,
      reassigned: false,
      canceled: false,
      passengers: parseInt(formData.passengers) || 1,
      selectedAddOnIds: (formData.selectedAddOnIds && formData.selectedAddOnIds.length > 0)
        ? JSON.stringify(formData.selectedAddOnIds)
        : null,
      carType: formData.carType || 1,
      isReoccurring: false,
      flightNumber: formData.flightNumber || null,
      accountId: linkedAccount?.accountId || null,
      accountDiscountAmount: (() => {
        if (!skipAccountDiscount && linkedAccount && !priceManuallyEdited && pricingDetails) {
          const addOnsTotal = (formData.selectedAddOnIds || []).reduce(
            (sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0
          );
          const rawCost = Math.round(pricingDetails.totalPrice) + addOnsTotal;
          return Math.max(0, rawCost - (parseInt(formData.cost) || 0));
        }
        return 0;
      })(),
      stopsCost: pricingDetails?.stopsCost ?? null,
      stopsDriverComp: pricingDetails?.stopsDriverComp ?? null,
      route: {
        routeId: 0,
        pickup: formData.pickup,
        dropOff: formData.dropOff,
        stop1: formData.additionalStops[0] || null,
        stop2: formData.additionalStops[1] || null,
        stop3: formData.additionalStops[2] || null,
        stop4: formData.additionalStops[3] || null,
        stop5: formData.additionalStops[4] || null,
        stop6: formData.additionalStops[5] || null,
        stop7: formData.additionalStops[6] || null,
        stop8: formData.additionalStops[7] || null,
        stop9: formData.additionalStops[8] || null,
        stop10: formData.additionalStops[9] || null,
        roundTrip: formData.roundTrip,
        callTime: currentTime,
        estimatedDuration: estimatedDurationTime,

      }
    };

    return rideObject;
  };

  const handlePaymentTokenGenerated = async (nonce, cardholderName) => {
    try {
      // Exchange the short-lived nonce for a permanent Square card on file.
      // Nonces expire in ~24 hours; card IDs (ccof:...) never expire.
      const customerName = formData.customerName || cardholderName || 'Unknown';
      const result = await paymentAPI.saveCardOnFile(nonce, customerName);
      if (result.success) {
        setFormData(prev => ({ ...prev, paymentTokenId: result.cardId }));
        showToast(`${result.cardBrand} ending in ${result.last4} saved on file`, 'success');
      } else {
        showAlert('Error', `Failed to save card: ${result.message}`, [{ text: 'OK' }], 'error');
      }
    } catch (err) {
      console.error('❌ SaveCardOnFile error:', err);
      showAlert('Error', 'Failed to save card on file. Please try again.', [{ text: 'OK' }], 'error');
    }
  };

  const handleTokenError = (error) => {
    console.error('❌ Tokenization error:', error);
    showAlert('Error', `Failed to process card: ${error}`, [{ text: 'OK' }]);
  };

  const handleAccountSelect = (account) => {
    setLinkedAccount(account);
    setSkipAccountDiscount(false); // reset waiver when a new account is linked
    setSaveCardToAccount(false);
    setFormData(prev => {
      const updates = {};
      if (account.name) updates.customerName = account.name;
      if (!prev.customerPhoneNumber && account.phoneNumber) updates.customerPhoneNumber = account.phoneNumber;
      if (!prev.pickup && account.defaultPickup) {
        updates.pickup = account.defaultPickup;
        setAddressesValidated(v => ({ ...v, pickup: true }));
      }
      if (!prev.dropOff && account.defaultDropoff) {
        updates.dropOff = account.defaultDropoff;
        setAddressesValidated(v => ({ ...v, dropOff: true }));
      }
      if (prev.passengers === 1 && account.defaultPassengers) updates.passengers = account.defaultPassengers;
      if (account.defaultCarType) updates.carType = account.defaultCarType;
      // Apply account discount to already-calculated price
      if (account.discountType && account.discountValue > 0 && prev.cost > 0 && !priceManuallyEdited) {
        if (account.discountType === 'percent') {
          updates.cost = Math.max(0, Math.round(prev.cost * (1 - Math.min(account.discountValue, 100) / 100)));
        } else {
          updates.cost = Math.max(0, prev.cost - account.discountValue);
        }
      }
      // Auto-select account card only if no payment method configured yet
      if (!prev.paymentTokenId && account.cardId) {
        updates.paymentTokenId = account.cardId;
        setCardFromAccount(true);
      }
      return { ...prev, ...updates };
    });
  };

  const handleToggleSkipDiscount = (skip) => {
    setSkipAccountDiscount(skip);
    if (!priceManuallyEdited && pricingDetails && linkedAccount && linkedAccount.discountType && linkedAccount.discountValue > 0) {
      const addOnsTotal = (formData.selectedAddOnIds || []).reduce(
        (sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0
      );
      const rawCost = Math.round(pricingDetails.totalPrice) + addOnsTotal;
      if (skip) {
        setFormData(prev => ({ ...prev, cost: rawCost }));
      } else {
        let discounted;
        if (linkedAccount.discountType === 'percent') {
          discounted = Math.max(0, Math.round(rawCost * (1 - Math.min(linkedAccount.discountValue, 100) / 100)));
        } else {
          discounted = Math.max(0, rawCost - linkedAccount.discountValue);
        }
        setFormData(prev => ({ ...prev, cost: discounted }));
      }
    }
  };

  const handleRemoveAccount = () => {
    // Restore raw price (reverse the discount) if it was applied
    if (linkedAccount && linkedAccount.discountType && linkedAccount.discountValue > 0 && !skipAccountDiscount && !priceManuallyEdited && pricingDetails) {
      const addOnsTotal = (formData.selectedAddOnIds || []).reduce(
        (sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0
      );
      const rawCost = Math.round(pricingDetails.totalPrice) + addOnsTotal;
      setFormData(prev => ({ ...prev, cost: rawCost }));
    }
    setLinkedAccount(null);
    setSkipAccountDiscount(false);
    setSaveCardToAccount(false);
    if (cardFromAccount) {
      setFormData(prev => ({ ...prev, paymentTokenId: null }));
      setCardFromAccount(false);
    }
  };

  const handleSubmit = async () => {
    // Validate form
    if (!validateForm()) return;

    // If CC accordion is expanded but card not tokenized, show error
    if (ccAccordionExpanded && !formData.paymentTokenId) {
      showAlert(
        'Card Not Saved',
        'You have the credit card form open but haven\'t saved the card yet.\n\nPlease either:\n• Click "Save Card" to save the card\n• Or collapse the credit card section if you don\'t want to add a card',
        [{ text: 'OK' }],
        'warning'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const newRideData = buildNewRideObject();
      // Stamp a one-time idempotency key so the server can ignore retries/re-submits
      newRideData.clientRequestId = crypto.randomUUID();

      // Use safeInvoke without retries — NewCallCreated is NOT idempotent (each call creates a new ride)
      const result = await safeInvoke({
        connection: signalRConnection,
        connectFn: connectSignalR,
        methodName: 'NewCallCreated',
        args: [newRideData, null, saveCardToAccount],
        maxRetries: 0,
      });

      if (!result.success) {
        console.error('Failed to create call:', result.error);
        const errMsg = result.error?.message || result.error || 'Unknown error';
        showAlert(
          'Failed to Create Call',
          `The call could not be created. Please try again.\n\nDetails: ${errMsg}`,
          [{ text: 'OK' }],
          'error'
        );
        return;
      }

      onComplete(newRideData);

      clearForm();

      // Play success sound for call sent
      soundService.playCallSentSound();

      showToast('Call sent!', 'success');
    } catch (error) {
      console.error('Failed to create ride:', error);
      showAlert('Error', 'Failed to create ride. Please try again.', [{ text: 'OK' }], 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleClick = () => {
    if (!validateForm()) return;
    setShowDatePicker(true);
    // Scroll to schedule section after it renders
    setTimeout(() => {
      scheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleScheduleSubmit = async () => {
    if (!scheduledDate || !scheduledTime) {
      showAlert('Missing Information', 'Please select both date and time for scheduling', [{ text: 'OK' }], 'warning');
      return;
    }

    await handleSubmit();
    // Clear schedule fields and hide the schedule section
    setScheduledDate('');
    setScheduledTime('');
    setShowDatePicker(false);
  };

  const clearForm = () => {
    setFormData({
      customerName: '',
      customerPhoneNumber: '',
      cost: 0,
      driversCompensation: 0,
      notes: '',
      paymentType: 'cash', // Default to cash
      passengers: 1,
      selectedAddOnIds: [],
      carType: '',
      assignedToId: null,
      assignedToName: '',
      pickup: '',
      dropOff: '',
      roundTrip: false,
      additionalStops: [],
      paymentTokenId: null
    });
    setErrors({});
    // Reset trip estimate and validation states
    setRouteInfo({ distance: '', duration: '' });
    setAddressesValidated({ pickup: false, dropOff: false });
    setStopsValidated([]);
    setPricingDetails(null);
    setCcAccordionExpanded(false);
    setLinkedAccount(null);
    setCardFromAccount(false);
    setSaveCardToAccount(false);
    // Reset Square payment form
    if (squarePaymentRef.current) {
      squarePaymentRef.current.reset();
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    // Get local date in YYYY-MM-DD format
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">New Call Request</Typography>
          <IconButton onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Left Side - Call Details */}
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Call Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <AccountAutocomplete
                      label="Customer Name"
                      value={formData.customerName}
                      onChange={(text) => handleInputChange('customerName', text)}
                      onAccountSelect={handleAccountSelect}
                      linkedAccount={linkedAccount}
                      onRemoveAccount={handleRemoveAccount}
                      error={!!errors.customerName}
                      helperText={errors.customerName}
                      placeholder="Enter name or search accounts…"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Customer Number"
                      type="tel"
                      value={formData.customerPhoneNumber}
                      onChange={(e) => handleInputChange('customerPhoneNumber', e.target.value)}
                      onKeyDown={(e) => handlePhoneBackspace(e, 'customerPhoneNumber')}
                      error={!!errors.customerPhoneNumber}
                      helperText={errors.customerPhoneNumber}
                      placeholder="(555) 123-4567"
                      inputRef={phoneInputRef}
                      inputProps={{ maxLength: 14 }}
                      margin="normal"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <AddressAutocomplete
                      label="Pickup Location"
                      value={formData.pickup}
                      onChange={(address, placeDetails) => handleAddressSelect('pickup', address, placeDetails)}
                      onInputChange={(value) => handleInputChange('pickup', value)}
                      error={!!errors.pickup}
                      helperText={errors.pickup}
                      placeholder="Enter pickup address"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <AddressAutocomplete
                      label="Drop-off Location"
                      value={formData.dropOff}
                      onChange={(address, placeDetails) => handleAddressSelect('dropOff', address, placeDetails)}
                      onInputChange={(value) => handleInputChange('dropOff', value)}
                      error={!!errors.dropOff}
                      helperText={errors.dropOff}
                      placeholder="Enter drop-off address"
                      required
                    />
                  </Grid>

                  {/* Flight Number - only show if pickup is an airport */}
                  {checkIsAirport(formData.pickup) && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Flight Number"
                        value={formData.flightNumber}
                        onChange={(e) => handleInputChange('flightNumber', e.target.value)}
                        placeholder="e.g., AA123"
                        margin="normal"
                      />
                    </Grid>
                  )}

                  {/* Passengers */}
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Passengers"
                      type="number"
                      value={formData.passengers}
                      onChange={(e) => handleInputChange('passengers', e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                      error={!!errors.passengers}
                      helperText={errors.passengers || 'Default: 1'}
                      inputProps={{ min: 1 }}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                    />
                  </Grid>

                  {/* Car Type */}
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth margin="normal" required>
                      <InputLabel shrink>Car Type</InputLabel>
                      <Select
                        value={formData.carType}
                        label="Car Type"
                        displayEmpty
                        notched
                        onChange={(e) => handleInputChange('carType', e.target.value)}
                      >
                        {carTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>



                  {/* Assigned To Driver */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={drivers}
                      getOptionLabel={(option) => option.name || ''}
                      value={drivers.find(d => d.id === formData.assignedToId) || null}
                      onChange={(e, newValue) => {
                        handleInputChange('assignedToId', newValue?.id || null);
                        handleInputChange('assignedToName', newValue?.name || '');
                      }}
                      sx={{ minWidth: 200 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign to Driver"
                          placeholder="Search drivers..."
                          margin="normal"
                          InputLabelProps={{ shrink: true }}
                          sx={{ '& .MuiInputBase-root': { minWidth: 180 } }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2.5, gap: -0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.roundTrip}
                            onChange={(e) => handleInputChange('roundTrip', e.target.checked)}
                            size="small"
                            sx={{ py: 0.25 }}
                          />
                        }
                        label={<Typography variant="caption">Round Trip</Typography>}
                        sx={{ m: 0, height: 24 }}
                      />
                      {addOns.map(addOn => (
                        <FormControlLabel
                          key={addOn.id}
                          control={
                            <Checkbox
                              checked={(formData.selectedAddOnIds || []).includes(addOn.id)}
                              onChange={(e) => {
                                const current = formData.selectedAddOnIds || [];
                                const updated = e.target.checked
                                  ? [...current, addOn.id]
                                  : current.filter(id => id !== addOn.id);
                                const diff = e.target.checked ? addOn.fee : -addOn.fee;
                                handleInputChange('selectedAddOnIds', updated);
                                // Add-on fee goes entirely to the driver
                                setFormData(prev => ({ ...prev, selectedAddOnIds: updated, cost: Math.max(0, prev.cost + diff), driversCompensation: Math.max(0, prev.driversCompensation + diff) }));
                              }}
                              color="primary"
                              size="small"
                              sx={{ py: 0.25 }}
                            />
                          }
                          label={<Typography variant="caption">{addOn.name} (+${addOn.fee})</Typography>}
                          sx={{ m: 0, height: 24 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>

                {/* Additional Stops */}
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      Additional Stops {formData.additionalStops.length > 0 && `(${formData.additionalStops.length}/10)`}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addStop}
                      variant="outlined"
                      disabled={formData.additionalStops.length >= 10}
                    >
                      Add Stop
                    </Button>
                  </Box>

                  {Array.isArray(formData.additionalStops) && formData.additionalStops.map((stop, index) => (
                    <Box key={index} display="flex" alignItems="flex-start" gap={1} mb={1}>
                      <Box sx={{ flex: 1 }}>
                        <AddressAutocomplete
                          ref={(el) => (stopRefs.current[index] = el)}
                          label={`Stop ${index + 1}`}
                          value={stop}
                          onChange={(address, placeDetails) => handleStopSelect(index, address, placeDetails)}
                          onInputChange={(value) => updateStop(index, value)}
                          placeholder={`Stop ${index + 1} address`}
                          size="small"
                          margin="none"
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeStop(index)}
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                {/* Route Info and Cost Row */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Route Info */}
                  {(routeInfo.distance || routeInfo.duration || isCalculatingRoute) && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Trip Estimate
                      </Typography>
                      {isCalculatingRoute ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Calculating...
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" gap={1}>
                          {routeInfo.distance && (
                            <Chip
                              label={routeInfo.distance}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          )}
                          {routeInfo.duration && (
                            <Chip
                              label={routeInfo.duration}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Pricing Section */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Pricing
                    </Typography>
                    {isCalculatingPrice ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Calculating price...
                        </Typography>
                      </Box>
                    ) : (
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" gap={1} alignItems="center">
                          {isEditingPrice ? (
                            <>
                              <TextField
                                size="small"
                                type="number"
                                value={formData.cost}
                                onChange={(e) => {
                                  handleInputChange('cost', parseFloat(e.target.value) || 0);
                                  setPriceManuallyEdited(true);
                                }}
                                label="Total"
                                InputProps={{
                                  startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography>,
                                }}
                                sx={{ width: '120px' }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                value={formData.driversCompensation}
                                onChange={(e) => {
                                  handleInputChange('driversCompensation', parseFloat(e.target.value) || 0);
                                  setPriceManuallyEdited(true);
                                }}
                                label="Driver"
                                InputProps={{
                                  startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography>,
                                }}
                                sx={{ width: '120px' }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setIsEditingPrice(false)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Chip
                                label={`Total: $${formData.cost.toFixed(2)}`}
                                color={errors.cost ? "error" : "primary"}
                                size="medium"
                              />
                              <Chip
                                label={`Driver: $${formData.driversCompensation.toFixed(2)}`}
                                color="success"
                                variant="outlined"
                                size="medium"
                              />
                              <IconButton
                                size="small"
                                onClick={() => setIsEditingPrice(true)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                        {/* Pricing breakdown */}
                        {pricingDetails && !isEditingPrice && (
                          <Box sx={{ px: 0.5, mt: 0.25 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {pricingDetails.pricingMethod === 'SET_PRICE'
                                ? `Set price · ${pricingDetails.originArea} → ${pricingDetails.destinationArea}`
                                : pricingDetails.hourlyRate > 0
                                  ? `Formula · hourly (${pricingDetails.tripHours?.toFixed(2)} hr × $${pricingDetails.hourlyRate?.toFixed(2)}/hr)`
                                  : pricingDetails.distanceMiles > 0
                                    ? `Formula · ${pricingDetails.distanceMiles.toFixed(1)} mi × $${pricingDetails.perMileRate?.toFixed(2)}/mi`
                                    : 'Formula pricing'}
                            </Typography>
                            {(pricingDetails.tollCost > 0 || pricingDetails.rushHourSurcharge > 0 || pricingDetails.stopsCost > 0 || pricingDetails.minimumFareApplied) && (() => {
                              const mileageBase = pricingDetails.mileageCost > 0
                                ? pricingDetails.mileageCost
                                : (pricingDetails.perMileRate > 0 && pricingDetails.distanceMiles > 0
                                  ? pricingDetails.perMileRate * pricingDetails.distanceMiles
                                  : (pricingDetails.basePrice || 0));
                              return (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {[
                                    `base $${mileageBase.toFixed(2)}`,
                                    pricingDetails.tollCost > 0 && `+ tolls $${pricingDetails.tollCost.toFixed(2)}`,
                                    pricingDetails.rushHourSurcharge > 0 && `+ rush $${pricingDetails.rushHourSurcharge.toFixed(2)}`,
                                    pricingDetails.stopsCost > 0 && `+ stops $${Math.round(pricingDetails.stopsCost)}`,
                                    pricingDetails.minimumFareApplied && `(min fare, was $${(pricingDetails.originalPrice || 0).toFixed(2)})`,
                                  ].filter(Boolean).join(' ')}
                                </Typography>
                              );
                            })()}
                          </Box>
                        )}
                        {errors.cost && (
                          <Typography variant="caption" color="error">
                            {errors.cost}
                          </Typography>
                        )}
                        {/* Account discount chip + waive toggle */}
                        {linkedAccount && linkedAccount.discountType && linkedAccount.discountValue > 0 && pricingDetails && !priceManuallyEdited && (() => {
                          const addOnsTotal = (formData.selectedAddOnIds || []).reduce((sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0);
                          const rawCost = Math.round(pricingDetails.totalPrice) + addOnsTotal;
                          const discountAmt = linkedAccount.discountType === 'percent'
                            ? Math.round(rawCost * Math.min(linkedAccount.discountValue, 100) / 100)
                            : Math.min(linkedAccount.discountValue, rawCost);
                          if (discountAmt <= 0) return null;
                          return (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                              {skipAccountDiscount ? (
                                <Chip size="small" variant="outlined" color="default" label="Discount waived" sx={{ textDecoration: 'line-through', opacity: 0.6 }} />
                              ) : (
                                <Chip size="small" color="success" label={`Discount (Account): -$${discountAmt.toFixed(2)}`} />
                              )}
                              <Button
                                size="small"
                                variant="text"
                                color={skipAccountDiscount ? 'success' : 'inherit'}
                                sx={{ fontSize: '0.7rem', minWidth: 0, px: 0.5, py: 0, textTransform: 'none', color: skipAccountDiscount ? undefined : 'text.secondary' }}
                                onClick={() => handleToggleSkipDiscount(!skipAccountDiscount)}
                              >
                                {skipAccountDiscount ? 'Apply discount' : 'Waive'}
                              </Button>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>

                  {/* Notes */}
                  <TextField
                    sx={{ flex: 1, minWidth: 200 }}
                    label="Notes"
                    multiline
                    rows={2}
                    size="small"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Additional notes..."
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Payment Info */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Default is cash. Add a credit card below to save on file (driver will charge at dropoff).
                </Typography>

                {/* Collapsible Credit Card Section */}
                <Accordion
                  expanded={ccAccordionExpanded && !formData.paymentTokenId}
                  onChange={(e, expanded) => setCcAccordionExpanded(expanded)}
                  TransitionProps={{ unmountOnExit: true }}
                  sx={{
                    mt: 2,
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: formData.paymentTokenId ? 'success.main' : 'divider',
                    borderRadius: '8px !important',
                    bgcolor: formData.paymentTokenId ? 'success.lighter' : 'background.paper'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }
                    }}
                  >
                    <CreditCardIcon color={formData.paymentTokenId ? 'success' : 'action'} />
                    <Typography>
                      {formData.paymentTokenId ? 'Credit Card Added ✓' : 'Add Credit Card (Optional)'}
                    </Typography>
                    {formData.paymentTokenId && (
                      <Chip
                        label="Card on File"
                        size="small"
                        color="success"
                        sx={{ ml: 'auto', mr: 1 }}
                      />
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Enter credit card details to save on file. Card will be tokenized securely (no raw card data is stored).
                    </Typography>

                    <PaymentForm
                      ref={squarePaymentRef}
                      onTokenGenerated={handlePaymentTokenGenerated}
                      onTokenCleared={() => {
                        setFormData(prev => ({ ...prev, paymentTokenId: null }));
                        setCardFromAccount(false);
                        setSaveCardToAccount(false);
                        setCcAccordionExpanded(true);
                      }}
                      onError={handleTokenError}
                    />

                    {/* "Save card to account" opt-in — only when a new nonce has been entered (not account's existing ccof) */}
                    {linkedAccount && formData.paymentTokenId && !cardFromAccount && (
                      <FormControlLabel
                        sx={{ mt: 1 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={saveCardToAccount}
                            onChange={e => setSaveCardToAccount(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            Save this card to account <strong>{linkedAccount.name}</strong> for future rides
                          </Typography>
                        }
                      />
                    )}

                    {errors.paymentTokenId && (
                      <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {errors.paymentTokenId}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Schedule Date/Time Picker */}
        {showDatePicker && (
          <Card ref={scheduleRef} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Schedule Call
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: getTodayDate() }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setShowDatePicker(false)}
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleScheduleSubmit}
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                >
                  {isSubmitting ? 'Scheduling...' : 'Confirm Schedule'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      {/* Bottom Actions */}
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleScheduleClick}
          disabled={isSubmitting || showDatePicker}
          startIcon={<ScheduleIcon />}
          color="secondary"
          variant="outlined"
        >
          Schedule Call
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || showDatePicker}
          startIcon={<SendIcon />}
          variant="contained"
          color="primary"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewCallWizard;